import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { appRoutes } from '@/routes';
import firebase, { db } from '../../config/firebase.config';
import httpClient from '../../config/httpClient';
import {
  addNotification,
  notificationStates,
} from '../../components/Notification';
import moment from 'moment-timezone';
import { useFormChanges } from '../../hooks/useFormChanges';
import { useStealthMode } from '../../hooks/useStealthMode';
import { useUserCompanies } from '../../hooks/useUserCompanies';
import { useUserTeamsForCompany } from '../../hooks/useUserTeamsForCompany';
import { useEmployeeListRoster } from '../../hooks/useEmployeeListRoster';
import {
  fetchUserTeamsForCompany,
  fetchUserCompanies,
} from '../../services/userMembership';
import teamDatabase from '@services/team';
import { fetchEmployeeExtendedDetails } from '../../services/employeeMemberEnrichment';
import { manageTeamMembersActionTypes } from '@store/ManageTeams/ManageTeams.actionTypes';
import { queryKeys } from '../../config/queryKeys';
import manageTeamsService from '../../services/manageTeams';
import { ROLE_OPTIONS } from '@/constants/roles';
import authService from '@services/auth';
import { useEmployeePageCompanyTeamUrlSync } from '@/hooks/useEmployeePageUrlSync';
import { useUrlParamSync } from '@/hooks/useUrlParamSync';
import { mergeEmployeePageFiltersIntoSearch } from '@/utils/employeePageUrlFilters';
import { EMPLOYEE_PAGE_QUERY } from '@/constants/urlQuery';
import { IntegrationImportTab } from './components/IntegrationImportTab.component';
import { BsBuildings } from 'react-icons/bs';
import {
  FiMail,
  FiCreditCard,
  FiGlobe,
  FiMonitor,
  FiHash,
  FiCalendar,
  FiShield,
  FiEye,
  FiEdit2,
  FiUserPlus,
  FiRefreshCw,
  FiTrash2,
} from 'react-icons/fi';
import { escapeCSVField, downloadCSV } from '@/utils/csvExport';
import { EmployeeListView } from './EmployeeList.view';

// Distinct from ROLE_OPTIONS above (used by the Edit dialog) — this powers
// the page-level Role filter pill, which needs an "All Roles" option.
const ROLE_FILTER_OPTIONS = [
  { label: 'All Roles', value: 'All' },
  { label: 'Admin', value: 'Admin' },
  { label: 'Manager', value: 'Manager' },
  { label: 'Member', value: 'Member' },
];
const ROLE_FILTER_VALUES = ROLE_FILTER_OPTIONS.map((o) => o.value);
const normalizeRoleFilter = (value) =>
  ROLE_FILTER_VALUES.includes(value) ? value : 'All';

const PAGE_SIZE = 10;

export const EmployeeListComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const authState = useSelector((state) => ({ ...state.auth }));
  const userProfile = authState.profile;
  const activeCompanyId = userProfile?.activeCompany?.companyId;
  const activeTeamId = useSelector(
    (s) => s.team?.active?.activeTeamid ?? s.team?.active?.activeTeamId
  );
  const userRole = userProfile?.role || '';
  const showAddTeamButton = userRole === 'Admin';
  const companyList = userProfile?.companyList || [];
  const userUID = userProfile?.id;
  const toast = React.useRef(null);
  const menuRef = useRef(null);
  const editFormHydratedRef = useRef(null);
  /** Set when saving a role change under stealth; consumed on UPDATE_EMPLOYEE_DETAILS success. */
  const pendingStealthAccessSyncRef = useRef(null);
  const {
    updateEmployeeDetailsSuccess,
    updateEmployeeDetailsPending,
    updateEmployeeDetailsFailure,
    updateEmployeeDetailsFailurePayload,
    removeUserFromTeamSuccess,
    removeUserFromTeamPending,
    removeUserFromTeamFailure,
    removeUserFromTeamFailurePayload,
  } = useSelector((state) => state.manageTeams?.members || {});

  // State
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [error, setError] = useState(null);
  const [moreDetailsUserId, setMoreDetailsUserId] = useState(null);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDeskId, setEditDeskId] = useState('');
  const [editTimeZone, setEditTimeZone] = useState('');
  const [editRole, setEditRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [activeTab, setActiveTab] = useState('employees');
  const [deleteFlowLoading, setDeleteFlowLoading] = useState(false);
  const [pickActiveTeamDialogVisible, setPickActiveTeamDialogVisible] =
    useState(false);
  const [replacementTeamOptions, setReplacementTeamOptions] = useState([]);
  const [selectedReplacementTeam, setSelectedReplacementTeam] = useState(null);
  const [pendingDeleteContext, setPendingDeleteContext] = useState(null);
  const [removeFromCompanyPending, setRemoveFromCompanyPending] =
    useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');
  const [deleteConfirmPayload, setDeleteConfirmPayload] = useState(null);
  const [removeFromCompanyConfirmVisible, setRemoveFromCompanyConfirmVisible] =
    useState(false);
  const [removeFromCompanyMessage, setRemoveFromCompanyMessage] = useState('');
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [teamListRefreshKey, setTeamListRefreshKey] = useState(0);
  const [addToAnotherTeamDialogVisible, setAddToAnotherTeamDialogVisible] =
    useState(false);
  const [addToAnotherTeamUser, setAddToAnotherTeamUser] = useState(null);
  // Existing company-wide admins already have access to every team, so the
  // "add to another company & department" membership step doesn't apply to
  // them — the dialog collapses to just the active tracking section.
  const isExistingTeamAdmin = addToAnotherTeamUser?.role === 'Admin';
  const [addToAnotherTeamCompany, setAddToAnotherTeamCompany] = useState(null);
  const [addToAnotherTeamRole, setAddToAnotherTeamRole] = useState(null);
  const [addToAnotherTeamTeams, setAddToAnotherTeamTeams] = useState([]);
  const [addToAnotherTeamCandidateTeams, setAddToAnotherTeamCandidateTeams] =
    useState([]);
  const [
    addToAnotherTeamCompanyTeamCount,
    setAddToAnotherTeamCompanyTeamCount,
  ] = useState(0);
  const [
    loadingAddToAnotherTeamCandidates,
    setLoadingAddToAnotherTeamCandidates,
  ] = useState(false);
  const [submittingAddToAnotherTeam, setSubmittingAddToAnotherTeam] =
    useState(false);
  const [
    addToAnotherTeamActiveCompanyOptions,
    setAddToAnotherTeamActiveCompanyOptions,
  ] = useState([]);
  const [addToAnotherTeamActiveCompany, setAddToAnotherTeamActiveCompany] =
    useState(null);
  const [
    addToAnotherTeamActiveTeamOptions,
    setAddToAnotherTeamActiveTeamOptions,
  ] = useState([]);
  const [addToAnotherTeamActiveTeam, setAddToAnotherTeamActiveTeam] =
    useState(null);
  const [addToAnotherTeamActiveBaseline, setAddToAnotherTeamActiveBaseline] =
    useState(null);
  const [
    loadingAddToAnotherTeamActiveSection,
    setLoadingAddToAnotherTeamActiveSection,
  ] = useState(false);

  const [changeActiveTeamDialogVisible, setChangeActiveTeamDialogVisible] =
    useState(false);
  const [changeActiveTeamUser, setChangeActiveTeamUser] = useState(null);
  const [changeActiveTeamCompanyOptions, setChangeActiveTeamCompanyOptions] =
    useState([]);
  const [changeActiveTeamCompany, setChangeActiveTeamCompany] = useState(null);
  const [changeActiveTeamTeamOptions, setChangeActiveTeamTeamOptions] =
    useState([]);
  const [changeActiveTeamTeam, setChangeActiveTeamTeam] = useState(null);
  const [loadingChangeActiveTeamModal, setLoadingChangeActiveTeamModal] =
    useState(false);
  const [submittingChangeActiveTeam, setSubmittingChangeActiveTeam] =
    useState(false);
  const [changeActiveTeamBaseline, setChangeActiveTeamBaseline] =
    useState(null);

  const addTeamDefaultCompanyId = useMemo(
    () => selectedCompany?.value ?? activeCompanyId,
    [selectedCompany?.value, activeCompanyId]
  );

  const { stealthMode, loading: stealthLoading } = useStealthMode(
    selectedCompany?.value
  );

  const timezoneOptions = moment.tz
    .names()
    .map((tz) => ({ label: tz, value: tz }));

  const {
    employees,
    isLoading: loadingEmployees,
    isError: rosterError,
    error: rosterQueryError,
  } = useEmployeeListRoster(selectedTeam?.value);

  const roleCounts = useMemo(
    () =>
      (employees || []).reduce(
        (acc, emp) => {
          acc.total += 1;
          if (emp.role === 'Admin') acc.admins += 1;
          if (emp.role === 'Manager') acc.managers += 1;
          return acc;
        },
        { total: 0, admins: 0, managers: 0 }
      ),
    [employees]
  );

  const userIdForExtendedFetch = moreDetailsUserId
    ? moreDetailsUserId
    : editDialogVisible && editRow?.userId
      ? editRow.userId
      : null;

  const {
    data: extendedDetails,
    isLoading: loadingExtendedDetails,
    isError: extendedDetailsError,
    error: extendedDetailsQueryError,
  } = useQuery({
    queryKey: queryKeys.employeeList.extendedDetails(userIdForExtendedFetch),
    queryFn: async () => {
      const data = await fetchEmployeeExtendedDetails(
        db,
        userIdForExtendedFetch
      );
      if (!data) {
        throw new Error('Member not found');
      }
      return data;
    },
    enabled: Boolean(userIdForExtendedFetch),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const invalidateEmployeeListData = useCallback(() => {
    if (selectedTeam?.value) {
      queryClient.invalidateQueries(
        queryKeys.teamMemberDetails.detail(selectedTeam.value)
      );
    }
    if (editRow?.userId) {
      queryClient.invalidateQueries(
        queryKeys.employeeList.extendedDetails(editRow.userId)
      );
    }
  }, [queryClient, selectedTeam?.value, editRow?.userId]);

  useLayoutEffect(() => {
    if (!editDialogVisible || !editRow?.userId || !extendedDetails) return;
    if (editFormHydratedRef.current === editRow.userId) return;
    editFormHydratedRef.current = editRow.userId;
    setEditName(
      editRow.name === 'N/A' || editRow.name == null ? '' : String(editRow.name)
    );
    setEditDeskId(
      extendedDetails.deskId === 'N/A' || !extendedDetails.deskId
        ? ''
        : String(extendedDetails.deskId)
    );
    setEditTimeZone(
      extendedDetails.timeZone === 'N/A' ? '' : String(extendedDetails.timeZone)
    );
    setEditRole(editRow.role === 'N/A' ? '' : editRow.role);
  }, [
    editDialogVisible,
    editRow?.userId,
    extendedDetails,
    editRow?.role,
    editRow?.name,
  ]);

  const {
    companyOptions,
    loading: loadingCompanies,
    error: companiesError,
  } = useUserCompanies(userUID);
  const {
    teamOptions,
    loading: loadingTeams,
    error: teamsError,
  } = useUserTeamsForCompany(
    userUID,
    selectedCompany?.value,
    teamListRefreshKey
  );

  useEmployeePageCompanyTeamUrlSync({
    companyOptions,
    teamOptions,
    selectedCompany,
    selectedTeam,
    setSelectedCompany,
    setSelectedTeam,
    history,
    location,
    defaultCompanyId: activeCompanyId,
    defaultTeamId: activeTeamId,
    teamDefaultOnlyWhenCompanyId: activeCompanyId,
    searchTerm,
    roleFilter,
  });
  useUrlParamSync({
    location,
    paramKey: EMPLOYEE_PAGE_QUERY.search,
    setValue: setSearchTerm,
  });
  useUrlParamSync({
    location,
    paramKey: EMPLOYEE_PAGE_QUERY.role,
    setValue: setRoleFilter,
    normalize: normalizeRoleFilter,
  });

  const dispatchRemoveUserFromTeam = useCallback(
    (payload) => {
      dispatch({
        type: manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM,
        payload,
      });
    },
    [dispatch]
  );

  const handleTeamCreated = useCallback(() => {
    setTeamListRefreshKey((k) => k + 1);
  }, []);

  const showDeleteConfirm = useCallback(({ message, payload }) => {
    setDeleteConfirmMessage(message);
    setDeleteConfirmPayload(payload);
    setDeleteConfirmVisible(true);
  }, []);

  const resetDeleteConfirm = useCallback(() => {
    setDeleteConfirmVisible(false);
    setDeleteConfirmMessage('');
    setDeleteConfirmPayload(null);
  }, []);

  const confirmDeleteUserFromTeam = useCallback(() => {
    if (deleteConfirmPayload) dispatchRemoveUserFromTeam(deleteConfirmPayload);
    resetDeleteConfirm();
  }, [deleteConfirmPayload, dispatchRemoveUserFromTeam, resetDeleteConfirm]);

  const getMemberActiveSettings = useCallback(async (userId) => {
    const memberDoc = await db.collection('membersDatabase').doc(userId).get();
    if (!memberDoc.exists) return { activeCompanyId: null, activeTeamId: null };
    const data = memberDoc.data() || {};
    return {
      activeCompanyId: data.activeCompanyId || null,
      activeTeamId: data.activeTeamId || null,
    };
  }, []);

  const getMemberTeamsForCompany = useCallback(async (userId, companyId) => {
    const res = await fetchUserTeamsForCompany(userId, companyId);
    if (!res.success) return [];
    return res.data;
  }, []);

  const buildTeamOptions = useCallback((teams) => {
    return (teams || [])
      .filter(Boolean)
      .map((t) => ({ label: t.teamName || t.teamId, value: t.teamId }));
  }, []);

  const resetPickActiveTeamDialog = useCallback(() => {
    setPickActiveTeamDialogVisible(false);
    setReplacementTeamOptions([]);
    setSelectedReplacementTeam(null);
    setPendingDeleteContext(null);
  }, []);

  const resetAddToAnotherTeamDialog = useCallback(() => {
    setAddToAnotherTeamDialogVisible(false);
    setAddToAnotherTeamUser(null);
    setAddToAnotherTeamRole(null);
    setAddToAnotherTeamTeams([]);
    setAddToAnotherTeamCandidateTeams([]);
    setAddToAnotherTeamCompanyTeamCount(0);
    setLoadingAddToAnotherTeamCandidates(false);
    setSubmittingAddToAnotherTeam(false);
    setAddToAnotherTeamActiveCompanyOptions([]);
    setAddToAnotherTeamActiveCompany(null);
    setAddToAnotherTeamActiveTeamOptions([]);
    setAddToAnotherTeamActiveTeam(null);
    setAddToAnotherTeamActiveBaseline(null);
    setLoadingAddToAnotherTeamActiveSection(false);
  }, []);

  const resetChangeActiveTeamDialog = useCallback(() => {
    setChangeActiveTeamBaseline(null);
    setChangeActiveTeamDialogVisible(false);
    setChangeActiveTeamUser(null);
    setChangeActiveTeamCompanyOptions([]);
    setChangeActiveTeamCompany(null);
    setChangeActiveTeamTeamOptions([]);
    setChangeActiveTeamTeam(null);
    setLoadingChangeActiveTeamModal(false);
    setSubmittingChangeActiveTeam(false);
  }, []);

  const loadTeamsForChangeActiveTeam = useCallback(
    async (memberUserId, companyId, preferredTeamId) => {
      const teams = await getMemberTeamsForCompany(memberUserId, companyId);
      const teamOpts = buildTeamOptions(teams);
      setChangeActiveTeamTeamOptions(teamOpts);
      if (!teamOpts.length) {
        setChangeActiveTeamTeam(null);
        return { teamOpts, selectedTeamValue: null };
      }
      const pick =
        preferredTeamId && teamOpts.some((t) => t.value === preferredTeamId)
          ? preferredTeamId
          : teamOpts[0].value;
      const selected = teamOpts.find((t) => t.value === pick) || teamOpts[0];
      setChangeActiveTeamTeam(selected);
      return { teamOpts, selectedTeamValue: selected.value };
    },
    [getMemberTeamsForCompany, buildTeamOptions]
  );

  const openChangeActiveTeamDialog = useCallback(async () => {
    if (!editRow?.userId) {
      addNotification({
        message: 'Missing user context',
        level: notificationStates.warning,
      });
      return;
    }
    menuRef.current?.hide();
    const rowUserId = editRow.userId;
    setChangeActiveTeamUser({
      userId: rowUserId,
      email: editRow.email,
      name: editRow.name,
      role: editRow.role === 'N/A' ? '' : editRow.role,
    });
    setChangeActiveTeamDialogVisible(true);
    setLoadingChangeActiveTeamModal(true);
    setChangeActiveTeamCompany(null);
    setChangeActiveTeamTeam(null);
    setChangeActiveTeamCompanyOptions([]);
    setChangeActiveTeamTeamOptions([]);
    setChangeActiveTeamBaseline(null);

    try {
      const [activeSettings, companiesRes] = await Promise.all([
        getMemberActiveSettings(rowUserId),
        fetchUserCompanies(rowUserId),
      ]);

      if (!companiesRes.success || !companiesRes.data?.length) {
        addNotification({
          message:
            companiesRes.error || 'This member has no companies on file.',
          level: notificationStates.warning,
        });
        resetChangeActiveTeamDialog();
        return;
      }

      const companyOpts = companiesRes.data.map((c) => ({
        label: c.companyName || c.companyId,
        value: c.companyId,
      }));
      setChangeActiveTeamCompanyOptions(companyOpts);

      const validIds = new Set(companyOpts.map((o) => o.value));
      const fromMemberDoc =
        activeSettings.activeCompanyId &&
        validIds.has(activeSettings.activeCompanyId)
          ? activeSettings.activeCompanyId
          : null;
      const fromPage =
        selectedCompany?.value && validIds.has(selectedCompany.value)
          ? selectedCompany.value
          : null;
      const companyId = fromMemberDoc || fromPage || companyOpts[0].value;
      const companyOption =
        companyOpts.find((o) => o.value === companyId) || companyOpts[0];
      setChangeActiveTeamCompany(companyOption);

      const { selectedTeamValue } = await loadTeamsForChangeActiveTeam(
        rowUserId,
        companyOption.value,
        activeSettings.activeTeamId
      );
      setChangeActiveTeamBaseline({
        companyId: companyOption.value,
        teamId: selectedTeamValue,
      });
    } catch (e) {
      addNotification({
        message: e?.message || 'Failed to load active department settings',
        level: notificationStates.error,
      });
      resetChangeActiveTeamDialog();
    } finally {
      setLoadingChangeActiveTeamModal(false);
    }
  }, [
    editRow,
    selectedCompany?.value,
    getMemberActiveSettings,
    loadTeamsForChangeActiveTeam,
    resetChangeActiveTeamDialog,
  ]);

  const onChangeActiveTeamCompanySelect = useCallback(
    async (option) => {
      if (!changeActiveTeamUser?.userId || !option?.value) return;
      setChangeActiveTeamCompany(option);
      setChangeActiveTeamTeam(null);
      setLoadingChangeActiveTeamModal(true);
      try {
        await loadTeamsForChangeActiveTeam(
          changeActiveTeamUser.userId,
          option.value,
          null
        );
      } finally {
        setLoadingChangeActiveTeamModal(false);
      }
    },
    [changeActiveTeamUser?.userId, loadTeamsForChangeActiveTeam]
  );

  const submitChangeActiveTeam = useCallback(async () => {
    if (
      !changeActiveTeamUser?.userId ||
      !changeActiveTeamCompany?.value ||
      !changeActiveTeamTeam?.value
    ) {
      addNotification({
        message: 'Select company and active department',
        level: notificationStates.warning,
      });
      return;
    }
    setSubmittingChangeActiveTeam(true);
    try {
      const updateRes = await teamDatabase.updateMemberActiveCompanyAndTeam(
        changeActiveTeamUser.userId,
        changeActiveTeamCompany.value,
        changeActiveTeamTeam.value
      );
      if (!updateRes?.success) {
        throw new Error(
          updateRes?.error?.message ||
            updateRes?.error ||
            'Could not save active department'
        );
      }
      addNotification({
        message: `Active department saved: ${changeActiveTeamTeam.label}`,
        level: notificationStates.success,
      });
      const emailRes = await teamDatabase.sendActiveTeamChangeEmail({
        companyName: changeActiveTeamCompany.label || '',
        teamName: changeActiveTeamTeam.label || '',
      });
      if (!emailRes?.success) {
        addNotification({
          message:
            'Active department saved, but the confirmation email could not be sent.',
          level: notificationStates.warning,
        });
      }
      queryClient.invalidateQueries(
        queryKeys.employeeList.extendedDetails(changeActiveTeamUser.userId)
      );
      invalidateEmployeeListData();
      resetChangeActiveTeamDialog();
    } catch (e) {
      addNotification({
        message: e?.message || 'Failed to update active department',
        level: notificationStates.error,
      });
    } finally {
      setSubmittingChangeActiveTeam(false);
    }
  }, [
    changeActiveTeamUser,
    changeActiveTeamCompany,
    changeActiveTeamTeam,
    queryClient,
    invalidateEmployeeListData,
    resetChangeActiveTeamDialog,
  ]);

  const hasChangeActiveTeamPendingSave = useMemo(() => {
    if (
      !changeActiveTeamBaseline?.companyId ||
      !changeActiveTeamCompany?.value ||
      !changeActiveTeamTeam?.value
    ) {
      return false;
    }
    const companyChanged =
      changeActiveTeamCompany.value !== changeActiveTeamBaseline.companyId;
    const baselineTeam = changeActiveTeamBaseline.teamId ?? '';
    const currentTeam = changeActiveTeamTeam.value ?? '';
    const teamChanged = String(currentTeam) !== String(baselineTeam);
    return companyChanged || teamChanged;
  }, [changeActiveTeamBaseline, changeActiveTeamCompany, changeActiveTeamTeam]);

  const loadAddToAnotherTeamCandidates = useCallback(
    async ({ companyId, userId }) => {
      if (!companyId || !userId) {
        setAddToAnotherTeamCandidateTeams([]);
        setAddToAnotherTeamCompanyTeamCount(0);
        return;
      }

      setLoadingAddToAnotherTeamCandidates(true);
      try {
        const [companyTeamsRes, memberTeamsRes] = await Promise.all([
          fetchUserTeamsForCompany(userUID, companyId),
          fetchUserTeamsForCompany(userId, companyId),
        ]);

        if (!companyTeamsRes.success) {
          throw new Error(companyTeamsRes.message || 'Failed to load teams');
        }
        if (!memberTeamsRes.success) {
          throw new Error(
            memberTeamsRes.message || 'Failed to load member team list'
          );
        }

        const allCompanyTeams = companyTeamsRes.data || [];
        const memberTeams = memberTeamsRes.data || [];
        const memberTeamIds = new Set(memberTeams.map((t) => t.teamId));
        const candidateTeams = allCompanyTeams
          .filter((t) => !memberTeamIds.has(t.teamId))
          .map((t) => ({ label: t.teamName || t.teamId, value: t.teamId }));

        setAddToAnotherTeamCompanyTeamCount(allCompanyTeams.length);
        setAddToAnotherTeamCandidateTeams(candidateTeams);
        setAddToAnotherTeamTeams([]);
      } catch (e) {
        setAddToAnotherTeamCandidateTeams([]);
        setAddToAnotherTeamCompanyTeamCount(0);
        addNotification({
          message:
            e?.message ||
            'Failed to load departments for "Add to another department".',
          level: notificationStates.error,
        });
      } finally {
        setLoadingAddToAnotherTeamCandidates(false);
      }
    },
    [userUID]
  );

  // Filter roster (name, email, role from memberDetails)
  const filteredEmployees = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    return (employees || []).filter((emp) => {
      if (roleFilter !== 'All' && emp.role !== roleFilter) return false;
      if (!term) return true;
      const name = (emp.name || '').toLowerCase();
      const email = (emp.email || '').toLowerCase();
      const role = (emp.role || '').toLowerCase();
      return name.includes(term) || email.includes(term) || role.includes(term);
    });
  }, [employees, searchTerm, roleFilter]);

  const [sortedBy, setSortedBy] = useState(null);
  const [reverseSort, setReverseSort] = useState(false);
  const handleSortClick = (id, reverse) => {
    setSortedBy(id);
    setReverseSort(reverse);
  };

  const sortedEmployees = useMemo(() => {
    if (!sortedBy) return filteredEmployees;
    const sorted = [...filteredEmployees].sort((a, b) =>
      (a[sortedBy] || '').localeCompare(b[sortedBy] || '')
    );
    return reverseSort ? sorted.reverse() : sorted;
  }, [filteredEmployees, sortedBy, reverseSort]);

  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, sortedBy, reverseSort, selectedTeam?.value]);

  const totalPages = Math.max(1, Math.ceil(sortedEmployees.length / PAGE_SIZE));
  const pagedEmployees = sortedEmployees.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const moreDetailsRosterRow = useMemo(() => {
    if (!moreDetailsUserId) return null;
    if (editRow?.userId === moreDetailsUserId) return editRow;
    return employees.find((e) => e.userId === moreDetailsUserId) || null;
  }, [moreDetailsUserId, editRow, employees]);

  // Check if user is Admin
  useEffect(() => {
    const isAdmin =
      userProfile?.activeCompany?.role === 'Admin' ||
      companyList.some((company) => company.role === 'Admin');

    if (!isAdmin && !authState.loading) {
      setError('Access denied. Only Admin users can view employee list.');
    }
  }, [userProfile, companyList, authState.loading]);

  // When company is cleared, reset team (roster comes from React Query by teamId)
  useEffect(() => {
    if (!selectedCompany?.value) {
      setSelectedTeam(null);
    }
  }, [selectedCompany?.value]);

  // On update employee details success: close dialog, cleanup, invalidate queries
  useEffect(() => {
    if (updateEmployeeDetailsSuccess) {
      const stealthSyncPending = pendingStealthAccessSyncRef.current;
      pendingStealthAccessSyncRef.current = null;

      setEditDialogVisible(false);
      setEditRow(null);
      setEditName('');
      setEditDeskId('');
      setEditTimeZone('');
      setEditRole('');
      editFormHydratedRef.current = null;
      dispatch({
        type: manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS_CLEANUP,
      });
      invalidateEmployeeListData();
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Employee details updated successfully',
          life: 3000,
        });
      }

      if (stealthSyncPending) {
        const { userId, companyId, emailForPasswordReset } = stealthSyncPending;
        (async () => {
          try {
            const user = firebase.auth().currentUser;
            if (!user) return;
            const idToken = await user.getIdToken();
            const res = await httpClient.post(
              '/syncEmployeeAccessForCompany',
              { userId, companyId },
              { headers: { Authorization: `Bearer ${idToken}` } }
            );
            const accessSync = res.data?.data?.accessSync;
            if (accessSync?.action === 'granted' && emailForPasswordReset) {
              await authService.sendPasswordResetEmail({
                email: emailForPasswordReset,
              });
              if (toast.current) {
                toast.current.show({
                  severity: 'success',
                  summary: 'Access granted',
                  detail: 'Password reset email sent to the employee.',
                  life: 4000,
                });
              }
            } else if (accessSync?.action === 'revoked' && toast.current) {
              toast.current.show({
                severity: 'info',
                summary: 'Login disabled',
                detail: 'Login has been disabled for this member.',
                life: 4000,
              });
            }
          } catch (e) {
            if (toast.current) {
              toast.current.show({
                severity: 'error',
                summary: 'Access sync',
                detail:
                  e.response?.data?.message ||
                  e.message ||
                  'Failed to sync login access.',
                life: 4000,
              });
            }
          }
        })();
      }
    }
  }, [updateEmployeeDetailsSuccess, dispatch, invalidateEmployeeListData]);
  // Show error when update fails
  useEffect(() => {
    if (
      updateEmployeeDetailsFailure &&
      updateEmployeeDetailsFailurePayload?.message
    ) {
      pendingStealthAccessSyncRef.current = null;
      addNotification({
        message: updateEmployeeDetailsFailurePayload.message,
        level: notificationStates.error,
      });
      dispatch({
        type: manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS_CLEANUP,
      });
    }
  }, [
    updateEmployeeDetailsFailure,
    updateEmployeeDetailsFailurePayload,
    dispatch,
  ]);

  // On remove user from team success: cleanup, invalidate roster, toast
  useEffect(() => {
    if (removeUserFromTeamSuccess) {
      dispatch({
        type: manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM_CLEANUP,
      });
      invalidateEmployeeListData();
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'User removed from team',
          life: 3000,
        });
      }
    }
  }, [removeUserFromTeamSuccess, dispatch, invalidateEmployeeListData]);

  // Show error when remove user fails
  useEffect(() => {
    if (
      removeUserFromTeamFailure &&
      removeUserFromTeamFailurePayload?.message
    ) {
      addNotification({
        message: removeUserFromTeamFailurePayload.message,
        level: notificationStates.error,
      });
      dispatch({
        type: manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM_CLEANUP,
      });
    }
  }, [removeUserFromTeamFailure, removeUserFromTeamFailurePayload, dispatch]);

  // CopyIconButton callbacks — shared by every copyable field in the "Employee details" popup
  const handleFieldCopied = (label) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Copied',
      detail: `${label} copied to clipboard`,
      life: 2000,
    });
  };

  const handleFieldCopyError = (err, label) => {
    console.error(`Failed to copy ${label}:`, err);
    addNotification({
      message: `Failed to copy ${label}`,
      level: notificationStates.error,
    });
  };

  const formatJoinedOn = (createdAt) => {
    if (!createdAt) return 'N/A';
    const date =
      typeof createdAt?.toDate === 'function'
        ? createdAt.toDate()
        : new Date(createdAt);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return moment(date).format('D MMM, YYYY');
  };

  // Escape CSV field (handles quotes and commas)
  // Export to CSV (exports current view: filtered list when search is active)
  const exportToCSV = () => {
    if (filteredEmployees.length === 0) {
      addNotification({
        message: searchTerm.trim()
          ? 'No data to export (no employees match your search).'
          : 'No data to export',
        level: notificationStates.warning,
      });
      return;
    }

    // Get company and team names for filename
    const companyName =
      companyOptions.find((c) => c.value === selectedCompany?.value)?.label ||
      'company';
    const teamName =
      teamOptions.find((t) => t.value === selectedTeam?.value)?.label ||
      'department';
    const date = new Date().toISOString().split('T')[0];
    const filename =
      `employee-list-${companyName}-${teamName}-${date}.csv`.replace(
        /[^a-z0-9-]/gi,
        '-'
      );

    // Roster export: memberDetails fields only (same as table)
    const headers = ['Name', 'Email', 'Role'];
    const rows = filteredEmployees.map((emp) =>
      [
        escapeCSVField(emp.name),
        escapeCSVField(emp.email),
        escapeCSVField(emp.role),
      ].join(',')
    );
    downloadCSV({ headers, rows, filename });

    addNotification({
      message: 'CSV exported successfully',
      level: notificationStates.success,
    });
  };

  const onActionsButtonClick = (e, rowData) => {
    setEditRow(rowData);
    menuRef.current.toggle(e);
  };

  const openMoreDetailsDialog = useCallback(() => {
    if (editRow?.userId) setMoreDetailsUserId(editRow.userId);
    menuRef.current?.hide();
  }, [editRow?.userId]);

  const openEditDialog = useCallback(() => {
    editFormHydratedRef.current = null;
    setEditDialogVisible(true);
    menuRef.current?.hide();
  }, []);

  const openDeleteConfirm = async () => {
    if (
      !editRow?.userId ||
      !selectedCompany?.value ||
      !selectedTeam?.value ||
      deleteFlowLoading
    )
      return;

    menuRef.current.hide();
    setDeleteFlowLoading(true);

    const userId = editRow.userId;
    const companyId = selectedCompany.value;
    const teamId = selectedTeam.value;

    try {
      const [teamsInCompany, active] = await Promise.all([
        getMemberTeamsForCompany(userId, companyId),
        getMemberActiveSettings(userId),
      ]);

      // Rule (1): cannot remove if user is only in one team (in this company)
      if (!teamsInCompany || teamsInCompany.length <= 1) {
        addNotification({
          message:
            'This user is only in one department. Add them to another department first, then delete.',
          level: notificationStates.warning,
        });
        return;
      }

      const isDeletingFromActiveTeam =
        active?.activeCompanyId === companyId &&
        active?.activeTeamId === teamId;

      // Rules (3) and (4): deleting from a non-active team keeps active team unchanged
      if (!isDeletingFromActiveTeam) {
        showDeleteConfirm({
          message:
            'Are you sure you want to delete this user from the department?',
          payload: { userId, companyId, teamId },
        });
        return;
      }

      const remainingTeams = teamsInCompany.filter((t) => t.teamId !== teamId);

      // Safety: should never happen if teamsInCompany.length > 1, but keep it defensive
      if (remainingTeams.length === 0) {
        addNotification({
          message:
            'Cannot delete user from their only department. Add them to another department first.',
          level: notificationStates.warning,
        });
        return;
      }

      // Rule (2): exactly two teams -> auto-switch active to the other team
      if (remainingTeams.length === 1) {
        const newActiveTeamId = remainingTeams[0].teamId;
        showDeleteConfirm({
          message:
            'This user is being removed from their active department. The other department will be set as active. Continue?',
          payload: { userId, companyId, teamId, newActiveTeamId },
        });
        return;
      }

      // Rule (5): multiple remaining teams -> ask which team should become active
      const options = buildTeamOptions(remainingTeams);
      setReplacementTeamOptions(options);
      setSelectedReplacementTeam(options[0] || null);
      setPendingDeleteContext({
        userId,
        companyId,
        teamId,
      });
      setPickActiveTeamDialogVisible(true);
    } catch (e) {
      console.error(
        '[EmployeeList delete] failed to build delete strategy:',
        e
      );
      addNotification({
        message:
          e?.message || 'Failed to prepare delete action. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setDeleteFlowLoading(false);
    }
  };

  const confirmDeleteAfterPickingActiveTeam = useCallback(() => {
    if (
      !pendingDeleteContext?.userId ||
      !pendingDeleteContext?.companyId ||
      !pendingDeleteContext?.teamId
    ) {
      resetPickActiveTeamDialog();
      return;
    }
    if (!selectedReplacementTeam?.value) {
      addNotification({
        message: 'Please select which department should be made active.',
        level: notificationStates.warning,
      });
      return;
    }

    const payload = {
      ...pendingDeleteContext,
      newActiveTeamId: selectedReplacementTeam.value,
    };

    resetPickActiveTeamDialog();
    showDeleteConfirm({
      message:
        'This user is being removed from their active department. The selected department will be set as active. Continue?',
      payload,
    });
  }, [
    pendingDeleteContext,
    selectedReplacementTeam,
    resetPickActiveTeamDialog,
    showDeleteConfirm,
  ]);

  const openRemoveFromCompanyConfirm = () => {
    if (!editRow?.userId || !selectedCompany?.value || removeFromCompanyPending)
      return;
    menuRef.current?.hide();
    const companyName =
      companyOptions.find((c) => c.value === selectedCompany.value)?.label ||
      selectedCompany.label ||
      'this company';
    const userName =
      editRow.name && editRow.name !== 'N/A' ? editRow.name : editRow.email;
    setRemoveFromCompanyMessage(
      `Remove ${userName} from ${companyName}? They will be removed from all departments in this company and their license will be revoked.`
    );
    setRemoveFromCompanyConfirmVisible(true);
  };

  const resetRemoveFromCompanyConfirm = () => {
    setRemoveFromCompanyConfirmVisible(false);
    setRemoveFromCompanyMessage('');
  };

  const confirmRemoveFromCompany = async () => {
    if (!editRow?.userId || !selectedCompany?.value) return;
    setRemoveFromCompanyPending(true);
    try {
      if (
        !userProfile?.subscription?.isTrialActive &&
        userProfile?.subscription?.status !== 'active'
      ) {
        addNotification({
          message:
            'Your subscription is not active. Please activate your subscription to remove employees.',
          level: notificationStates.error,
        });
        return;
      }
      const result = await manageTeamsService.removeUserFromCompany({
        userId: editRow.userId,
        companyId: selectedCompany.value,
      });
      if (result.success) {
        invalidateEmployeeListData();
        resetRemoveFromCompanyConfirm();
        if (toast.current) {
          toast.current.show({
            severity: 'success',
            summary: 'Success',
            detail: 'User removed from company and all its teams',
            life: 3000,
          });
        }
      } else {
        addNotification({
          message: result.message || 'Failed to remove user from company',
          level: notificationStates.error,
        });
      }
    } catch (e) {
      addNotification({
        message: e?.message || 'Failed to remove user from company',
        level: notificationStates.error,
      });
    } finally {
      setRemoveFromCompanyPending(false);
    }
  };

  const openAddToAnotherTeamDialog = useCallback(() => {
    if (!editRow?.userId || !editRow?.email) {
      addNotification({
        message: 'Missing user context',
        level: notificationStates.warning,
      });
      return;
    }
    const defaultCompanyOption =
      selectedCompany ||
      companyOptions.find((c) => c.value === activeCompanyId) ||
      null;
    setAddToAnotherTeamUser(editRow);
    setAddToAnotherTeamCompany(defaultCompanyOption);
    setAddToAnotherTeamRole(null);
    setAddToAnotherTeamTeams([]);
    setAddToAnotherTeamCandidateTeams([]);
    setAddToAnotherTeamCompanyTeamCount(0);
    setAddToAnotherTeamDialogVisible(true);
    menuRef.current?.hide();
  }, [editRow, selectedCompany, companyOptions, activeCompanyId]);

  useEffect(() => {
    if (!addToAnotherTeamDialogVisible) return;
    if (!addToAnotherTeamUser?.userId || !addToAnotherTeamCompany?.value)
      return;
    loadAddToAnotherTeamCandidates({
      companyId: addToAnotherTeamCompany.value,
      userId: addToAnotherTeamUser.userId,
    });
  }, [
    addToAnotherTeamDialogVisible,
    addToAnotherTeamCompany?.value,
    addToAnotherTeamUser?.userId,
    loadAddToAnotherTeamCandidates,
  ]);

  useEffect(() => {
    if (!addToAnotherTeamDialogVisible || !addToAnotherTeamUser?.userId) {
      return;
    }
    let cancelled = false;
    const uid = addToAnotherTeamUser.userId;

    (async () => {
      setLoadingAddToAnotherTeamActiveSection(true);
      setAddToAnotherTeamActiveBaseline(null);
      try {
        const [activeSettings, companiesRes] = await Promise.all([
          getMemberActiveSettings(uid),
          fetchUserCompanies(uid),
        ]);
        if (cancelled) return;

        if (!companiesRes.success || !companiesRes.data?.length) {
          setAddToAnotherTeamActiveCompanyOptions([]);
          setAddToAnotherTeamActiveCompany(null);
          setAddToAnotherTeamActiveTeamOptions([]);
          setAddToAnotherTeamActiveTeam(null);
          return;
        }

        const companyOpts = companiesRes.data.map((c) => ({
          label: c.companyName || c.companyId,
          value: c.companyId,
        }));
        setAddToAnotherTeamActiveCompanyOptions(companyOpts);

        const validIds = new Set(companyOpts.map((o) => o.value));
        const fromMemberDoc =
          activeSettings.activeCompanyId &&
          validIds.has(activeSettings.activeCompanyId)
            ? activeSettings.activeCompanyId
            : null;
        const pageCompanyId = selectedCompany?.value;
        const fromPage =
          pageCompanyId && validIds.has(pageCompanyId) ? pageCompanyId : null;
        const companyId = fromMemberDoc || fromPage || companyOpts[0].value;
        const companyOption =
          companyOpts.find((o) => o.value === companyId) || companyOpts[0];
        setAddToAnotherTeamActiveCompany(companyOption);

        const teams = await getMemberTeamsForCompany(uid, companyOption.value);
        if (cancelled) return;
        const teamOpts = buildTeamOptions(teams);
        setAddToAnotherTeamActiveTeamOptions(teamOpts);

        if (!teamOpts.length) {
          setAddToAnotherTeamActiveTeam(null);
          setAddToAnotherTeamActiveBaseline({
            companyId: companyOption.value,
            teamId: null,
          });
          return;
        }
        const pick =
          activeSettings.activeTeamId &&
          teamOpts.some((t) => t.value === activeSettings.activeTeamId)
            ? activeSettings.activeTeamId
            : teamOpts[0].value;
        const selected = teamOpts.find((t) => t.value === pick) || teamOpts[0];
        setAddToAnotherTeamActiveTeam(selected);
        setAddToAnotherTeamActiveBaseline({
          companyId: companyOption.value,
          teamId: selected.value,
        });
      } catch (e) {
        if (!cancelled) {
          console.error('[AddToAnotherTeam active section]', e);
          setAddToAnotherTeamActiveCompanyOptions([]);
          setAddToAnotherTeamActiveCompany(null);
          setAddToAnotherTeamActiveTeamOptions([]);
          setAddToAnotherTeamActiveTeam(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingAddToAnotherTeamActiveSection(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedCompany read on open only; omit so page filter changes do not reset modal
  }, [
    addToAnotherTeamDialogVisible,
    addToAnotherTeamUser?.userId,
    getMemberActiveSettings,
    getMemberTeamsForCompany,
    buildTeamOptions,
  ]);

  const onAddToAnotherTeamActiveCompanyChange = useCallback(
    async (option) => {
      if (!addToAnotherTeamUser?.userId || !option?.value) return;
      setAddToAnotherTeamActiveCompany(option);
      setAddToAnotherTeamActiveTeam(null);
      setLoadingAddToAnotherTeamActiveSection(true);
      try {
        const teams = await getMemberTeamsForCompany(
          addToAnotherTeamUser.userId,
          option.value
        );
        const teamOpts = buildTeamOptions(teams);
        setAddToAnotherTeamActiveTeamOptions(teamOpts);
        setAddToAnotherTeamActiveTeam(teamOpts[0] || null);
      } finally {
        setLoadingAddToAnotherTeamActiveSection(false);
      }
    },
    [addToAnotherTeamUser?.userId, getMemberTeamsForCompany, buildTeamOptions]
  );

  // Dirty-check for the "Active company & department (tracking)" section —
  // reused both to gate the Save button (only enabled once something
  // actually changed) and inside the save handler below (only call the
  // backend if there's really a change to persist).
  const addToAnotherTeamActiveInitialValues = useMemo(
    () => ({
      companyId: addToAnotherTeamActiveBaseline?.companyId ?? '',
      teamId: addToAnotherTeamActiveBaseline?.teamId ?? '',
    }),
    [addToAnotherTeamActiveBaseline]
  );
  const addToAnotherTeamActiveCurrentValues = {
    companyId: addToAnotherTeamActiveCompany?.value ?? '',
    teamId: addToAnotherTeamActiveTeam?.value ?? '',
  };
  const { hasChanges: hasActiveTrackingChanges } = useFormChanges(
    addToAnotherTeamActiveInitialValues,
    addToAnotherTeamActiveCurrentValues,
    ['companyId', 'teamId']
  );

  // Shared by both the regular and admin-only save paths below: pushes the
  // "active company/department" selection to the backend if it actually
  // changed from the baseline, and notifies accordingly.
  const updateActiveCompanyAndTeamIfChanged = useCallback(async () => {
    const wantsActiveUpdate =
      addToAnotherTeamActiveBaseline &&
      addToAnotherTeamActiveCompany?.value &&
      addToAnotherTeamActiveTeam?.value &&
      hasActiveTrackingChanges;

    if (!wantsActiveUpdate) return;

    const activeRes = await teamDatabase.updateMemberActiveCompanyAndTeam(
      addToAnotherTeamUser.userId,
      addToAnotherTeamActiveCompany.value,
      addToAnotherTeamActiveTeam.value
    );
    if (!activeRes?.success) {
      addNotification({
        message:
          activeRes?.error?.message ||
          activeRes?.error ||
          'Active company/department could not be saved.',
        level: notificationStates.warning,
      });
      return;
    }

    addNotification({
      message: `Active company and department updated for time and activity: ${addToAnotherTeamActiveTeam.label}.`,
      level: notificationStates.success,
    });
    const emailRes = await teamDatabase.sendActiveTeamChangeEmail({
      companyName: addToAnotherTeamActiveCompany.label || '',
      teamName: addToAnotherTeamActiveTeam.label || '',
    });
    if (!emailRes?.success) {
      addNotification({
        message:
          'Active department saved, but the confirmation email could not be sent.',
        level: notificationStates.warning,
      });
    }
    queryClient.invalidateQueries(
      queryKeys.employeeList.extendedDetails(addToAnotherTeamUser.userId)
    );
  }, [
    addToAnotherTeamUser,
    addToAnotherTeamActiveBaseline,
    addToAnotherTeamActiveCompany,
    addToAnotherTeamActiveTeam,
    hasActiveTrackingChanges,
    queryClient,
  ]);

  const submitAddToAnotherTeam = useCallback(async () => {
    if (!addToAnotherTeamUser?.userId) {
      addNotification({
        message: 'Please select company and user',
        level: notificationStates.warning,
      });
      return;
    }

    // Existing company-wide admins already have access to every team, so
    // this dialog only manages where their time/activity gets tracked —
    // skip the membership-granting call entirely for them.
    if (isExistingTeamAdmin) {
      setSubmittingAddToAnotherTeam(true);
      try {
        await updateActiveCompanyAndTeamIfChanged();
        invalidateEmployeeListData();
        resetAddToAnotherTeamDialog();
      } catch (e) {
        addNotification({
          message:
            e?.response?.data?.message ||
            e?.message ||
            'Failed to update tracking settings',
          level: notificationStates.error,
        });
      } finally {
        setSubmittingAddToAnotherTeam(false);
      }
      return;
    }

    if (!addToAnotherTeamCompany?.value) {
      addNotification({
        message: 'Please select company and user',
        level: notificationStates.warning,
      });
      return;
    }
    if (!addToAnotherTeamRole?.value) {
      addNotification({
        message: 'Please select role',
        level: notificationStates.warning,
      });
      return;
    }

    const isAdminPromotion = addToAnotherTeamRole.value === 'Admin';
    if (!isAdminPromotion && addToAnotherTeamTeams.length === 0) {
      addNotification({
        message: 'Please select at least one team',
        level: notificationStates.warning,
      });
      return;
    }

    setSubmittingAddToAnotherTeam(true);
    try {
      const user = firebase.auth().currentUser;
      if (!user) throw new Error('User not authenticated');
      const idToken = await user.getIdToken();
      if (isAdminPromotion) {
        const response = await httpClient.post(
          '/promoteUserToCompanyAdmin',
          {
            userId: addToAnotherTeamUser.userId,
            companyId: addToAnotherTeamCompany.value,
          },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        if (!response?.data?.success) {
          throw new Error(response?.data?.message || 'Failed to promote user');
        }
      } else {
        const response = await httpClient.post(
          '/addUserToTeams',
          {
            userId: addToAnotherTeamUser.userId,
            companyId: addToAnotherTeamCompany.value,
            teamIds: addToAnotherTeamTeams.map((t) => t.value),
            role: addToAnotherTeamRole.value,
          },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        if (!response?.data?.success) {
          throw new Error(
            response?.data?.message || 'Failed to add user to departments'
          );
        }
      }

      addNotification({
        message:
          addToAnotherTeamRole.value === 'Admin'
            ? 'User promoted to Admin and synced across company departments.'
            : 'User added to selected departments successfully.',
        level: notificationStates.success,
      });

      await updateActiveCompanyAndTeamIfChanged();

      invalidateEmployeeListData();
      resetAddToAnotherTeamDialog();
    } catch (e) {
      addNotification({
        message:
          e?.response?.data?.message ||
          e?.message ||
          'Failed to add user to another department',
        level: notificationStates.error,
      });
    } finally {
      setSubmittingAddToAnotherTeam(false);
    }
  }, [
    addToAnotherTeamUser,
    isExistingTeamAdmin,
    addToAnotherTeamCompany,
    addToAnotherTeamRole,
    addToAnotherTeamTeams,
    updateActiveCompanyAndTeamIfChanged,
    invalidateEmployeeListData,
    resetAddToAnotherTeamDialog,
  ]);

  const actionsMenuItems = [
    {
      label: 'View details',
      icon: <FiEye size={16} />,
      onClick: openMoreDetailsDialog,
    },
    {
      label: 'Edit employee',
      icon: <FiEdit2 size={16} />,
      onClick: openEditDialog,
    },
    {
      label: 'Add to another department',
      icon: <FiUserPlus size={16} />,
      onClick: openAddToAnotherTeamDialog,
    },
    {
      label: 'Change active department',
      icon: <FiRefreshCw size={16} />,
      onClick: openChangeActiveTeamDialog,
    },
    {
      label: 'Remove from department',
      icon: <FiTrash2 size={16} />,
      onClick: openDeleteConfirm,
    },
    {
      label: 'Remove from company',
      icon: <BsBuildings size={16} />,
      onClick: openRemoveFromCompanyConfirm,
    },
  ];

  const editFormInitialValues = useMemo(() => {
    if (!editRow || !extendedDetails) {
      return { name: '', deskId: '', timeZone: '', role: '' };
    }
    return {
      name:
        editRow.name === 'N/A' || editRow.name == null
          ? ''
          : String(editRow.name),
      deskId:
        extendedDetails.deskId === 'N/A' || !extendedDetails.deskId
          ? ''
          : String(extendedDetails.deskId),
      timeZone:
        extendedDetails.timeZone === 'N/A'
          ? ''
          : String(extendedDetails.timeZone),
      role: editRow.role === 'N/A' ? '' : editRow.role,
    };
  }, [editRow, extendedDetails]);
  const editFormCurrentValues = {
    name: editName,
    deskId: editDeskId,
    timeZone: editTimeZone,
    role: editRole,
  };
  const { hasChanges: hasEditFormChanges } = useFormChanges(
    editFormInitialValues,
    editFormCurrentValues,
    ['name', 'deskId', 'timeZone', 'role']
  );

  const saveEditDetails = async () => {
    if (!editRow?.userId || !selectedCompany?.value || !selectedTeam?.value) {
      addNotification({
        message: 'Missing context (user, company or team)',
        level: notificationStates.warning,
      });
      return;
    }
    if (!extendedDetails) {
      addNotification({
        message: 'Still loading member details. Try again.',
        level: notificationStates.warning,
      });
      return;
    }
    const currentDeskId =
      extendedDetails.deskId === 'N/A' || !extendedDetails.deskId
        ? ''
        : String(extendedDetails.deskId);
    const currentTimeZone =
      extendedDetails.timeZone === 'N/A' || !extendedDetails.timeZone
        ? ''
        : String(extendedDetails.timeZone);
    const currentRole = editRow.role === 'N/A' ? '' : editRow.role || '';
    const currentName =
      editRow.name === 'N/A' || editRow.name == null
        ? ''
        : String(editRow.name).trim();
    const newName = (editName || '').trim();
    const newDeskId = (editDeskId || '').trim();
    const newTimeZone = (editTimeZone || '').trim();
    const newRole =
      editRole && ROLE_OPTIONS.some((o) => o.value === editRole)
        ? editRole
        : null;
    const payload = {
      userId: editRow.userId,
      companyId: selectedCompany?.value,
      teamId: selectedTeam?.value,
    };
    if (newName !== currentName) {
      if (!newName) {
        addNotification({
          message: 'Name cannot be empty',
          level: notificationStates.warning,
        });
        return;
      }
      payload.name = newName;
    }
    if (newDeskId !== currentDeskId) payload.deskId = newDeskId;
    if (newTimeZone !== currentTimeZone && newTimeZone)
      payload.timeZone = newTimeZone;
    if (newRole !== null && newRole !== currentRole) payload.role = newRole;
    const hasChanges =
      payload.deskId !== undefined ||
      payload.timeZone !== undefined ||
      payload.role !== undefined ||
      payload.name !== undefined;
    if (!hasChanges) {
      addNotification({
        message: 'No changes to save',
        level: notificationStates.warning,
      });
      return;
    }
    if (payload.deskId !== undefined && !payload.deskId) {
      addNotification({
        message: 'Desk Id cannot be empty when changing',
        level: notificationStates.warning,
      });
      return;
    }
    const emailToSend =
      editRow.email && editRow.email !== 'N/A' ? editRow.email.trim() : null;
    if (!stealthLoading && stealthMode && payload.role !== undefined) {
      pendingStealthAccessSyncRef.current = {
        userId: editRow.userId,
        companyId: selectedCompany.value,
        emailForPasswordReset: emailToSend,
      };
    } else {
      pendingStealthAccessSyncRef.current = null;
    }

    dispatch({
      type: manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS,
      payload,
    });

    if (newRole !== currentRole && emailToSend) {
      try {
        const functions = firebase.functions();
        const sendMailForEmployeeRoleChange = functions.httpsCallable(
          'sendMailForEmployeeRoleChange'
        );
        const result = await sendMailForEmployeeRoleChange({
          email: emailToSend,
          newRole,
          currentRole,
          companyName: selectedCompany?.label,
          teamName: selectedTeam?.label,
          userName: editRow.name,
        });
      } catch (error) {
        console.error(error);
        addNotification({
          message: 'Failed to send email for employee role change',
          level: notificationStates.error,
        });
      }
    }
  };

  const employeeDetailFields = useMemo(() => {
    if (!moreDetailsRosterRow || !extendedDetails) return [];
    return [
      {
        key: 'email',
        icon: <FiMail size={14} color="var(--blue-600)" />,
        iconBg: 'var(--blue-100)',
        label: 'Email',
        value: moreDetailsRosterRow.email,
        copyLabel: 'Email',
        fullWidth: true,
      },
      {
        key: 'employeeId',
        icon: <FiCreditCard size={14} color="var(--purple-600)" />,
        iconBg: 'var(--purple-100)',
        label: 'Employee ID',
        value: extendedDetails.employeeId,
        copyLabel: 'Employee ID',
      },
      {
        key: 'role',
        icon: <FiShield size={14} color="var(--success-600)" />,
        iconBg: 'var(--success-100)',
        label: 'Role',
        value: moreDetailsRosterRow.role,
      },
      {
        key: 'timeZone',
        icon: <FiGlobe size={14} color="var(--warning-600)" />,
        iconBg: 'var(--warning-100)',
        label: 'Time Zone',
        value: extendedDetails.timeZone,
      },
      {
        key: 'deskId',
        icon: <FiMonitor size={14} color="var(--gray-600)" />,
        iconBg: 'var(--gray-100)',
        label: 'Desk ID',
        value: extendedDetails.deskId,
      },
      {
        key: 'guid',
        icon: <FiHash size={14} color="var(--gray-600)" />,
        iconBg: 'var(--gray-100)',
        label: 'GUID',
        value: extendedDetails.guid,
        copyLabel: 'GUID',
        mono: true,
        truncate: true,
      },
      {
        key: 'joinedOn',
        icon: <FiCalendar size={14} color="var(--blue-600)" />,
        iconBg: 'var(--blue-100)',
        label: 'Joined On',
        value: formatJoinedOn(extendedDetails.createdAt),
      },
    ];
  }, [moreDetailsRosterRow, extendedDetails]);

  // ── View wiring ────────────────────────────────────────────────────────
  // Handlers extracted verbatim from the inline JSX callbacks that used to
  // live in the render below — same logic, just named so EmployeeList.view.jsx
  // (pure presentational) can receive them as props instead of closing over
  // container state directly.
  const onTeamChange = useCallback(
    (val) => {
      setSelectedTeam(teamOptions.find((o) => o.value === val) || null);
      setError(null);
    },
    [teamOptions]
  );

  const onInviteEmployeeClick = useCallback(() => {
    history.push({
      pathname: appRoutes().addEmployee,
      search: mergeEmployeePageFiltersIntoSearch(location.search, {
        companyId: selectedCompany?.value ?? '',
        teamId: selectedTeam?.value ?? '',
      }),
    });
  }, [history, location.search, selectedCompany?.value, selectedTeam?.value]);

  const onAddDepartmentClick = useCallback(() => {
    setShowAddTeamModal(true);
  }, []);

  const onCloseAddTeamModal = useCallback(() => {
    setShowAddTeamModal(false);
  }, []);

  const onCloseMoreDetailsDialog = useCallback(() => {
    setMoreDetailsUserId(null);
  }, []);

  const onAddToAnotherTeamCompanyChange = useCallback((option) => {
    setAddToAnotherTeamCompany(option);
    setAddToAnotherTeamTeams([]);
  }, []);

  const onAddToAnotherTeamTeamsChange = useCallback((option) => {
    setAddToAnotherTeamTeams(Array.isArray(option) ? option : []);
  }, []);

  const onHideEditDialog = useCallback(() => {
    setEditDialogVisible(false);
    setEditRow(null);
    setEditName('');
    setEditDeskId('');
    setEditTimeZone('');
    setEditRole('');
    editFormHydratedRef.current = null;
  }, []);

  const onCancelEditDialog = useCallback(() => {
    setEditDialogVisible(false);
    editFormHydratedRef.current = null;
  }, []);

  const onEditDeskIdChange = useCallback((e) => {
    setEditDeskId(e.target.value);
  }, []);

  const onEditRoleChange = useCallback((e) => {
    setEditRole(e.value || '');
  }, []);

  const onEditTimeZoneChange = useCallback((e) => {
    setEditTimeZone(e.value || '');
  }, []);

  const onSearchTermChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // The "Access denied" branch (error set + non-Admin) is rendered inside
  // EmployeeListView itself (from the `error` / `userProfile` props below) so
  // it stays defined in exactly one place, shared with demo mode.
  return (
    <EmployeeListView
      error={error}
      userProfile={userProfile}
      selectedCompany={selectedCompany}
      selectedTeam={selectedTeam}
      teamOptions={teamOptions}
      loadingTeams={loadingTeams}
      onTeamChange={onTeamChange}
      roleFilter={roleFilter}
      roleFilterOptions={ROLE_FILTER_OPTIONS}
      onRoleFilterChange={setRoleFilter}
      showAddTeamButton={showAddTeamButton}
      onInviteEmployeeClick={onInviteEmployeeClick}
      onAddDepartmentClick={onAddDepartmentClick}
      onExportCSV={exportToCSV}
      roleCounts={roleCounts}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      toastRef={toast}
      deleteConfirmVisible={deleteConfirmVisible}
      onHideDeleteConfirm={resetDeleteConfirm}
      deleteConfirmMessage={deleteConfirmMessage}
      onConfirmDeleteUserFromTeam={confirmDeleteUserFromTeam}
      removeFromCompanyConfirmVisible={removeFromCompanyConfirmVisible}
      onHideRemoveFromCompanyConfirm={resetRemoveFromCompanyConfirm}
      removeFromCompanyMessage={removeFromCompanyMessage}
      onConfirmRemoveFromCompany={confirmRemoveFromCompany}
      removeFromCompanyPending={removeFromCompanyPending}
      actionsMenuItems={actionsMenuItems}
      menuRef={menuRef}
      moreDetailsUserId={moreDetailsUserId}
      onCloseMoreDetailsDialog={onCloseMoreDetailsDialog}
      moreDetailsRosterRow={moreDetailsRosterRow}
      loadingExtendedDetails={loadingExtendedDetails}
      extendedDetailsError={extendedDetailsError}
      extendedDetailsQueryError={extendedDetailsQueryError}
      extendedDetails={extendedDetails}
      employeeDetailFields={employeeDetailFields}
      onFieldCopied={handleFieldCopied}
      onFieldCopyError={handleFieldCopyError}
      isExistingTeamAdmin={isExistingTeamAdmin}
      addToAnotherTeamDialogVisible={addToAnotherTeamDialogVisible}
      onCloseAddToAnotherTeamDialog={resetAddToAnotherTeamDialog}
      submittingAddToAnotherTeam={submittingAddToAnotherTeam}
      onSubmitAddToAnotherTeam={submitAddToAnotherTeam}
      loadingAddToAnotherTeamActiveSection={
        loadingAddToAnotherTeamActiveSection
      }
      hasActiveTrackingChanges={hasActiveTrackingChanges}
      loadingAddToAnotherTeamCandidates={loadingAddToAnotherTeamCandidates}
      addToAnotherTeamCompany={addToAnotherTeamCompany}
      addToAnotherTeamRole={addToAnotherTeamRole}
      addToAnotherTeamTeams={addToAnotherTeamTeams}
      companyOptions={companyOptions}
      onAddToAnotherTeamCompanyChange={onAddToAnotherTeamCompanyChange}
      onAddToAnotherTeamTeamsChange={onAddToAnotherTeamTeamsChange}
      onAddToAnotherTeamRoleChange={setAddToAnotherTeamRole}
      addToAnotherTeamCandidateTeams={addToAnotherTeamCandidateTeams}
      addToAnotherTeamCompanyTeamCount={addToAnotherTeamCompanyTeamCount}
      addToAnotherTeamUser={addToAnotherTeamUser}
      addToAnotherTeamActiveCompanyOptions={
        addToAnotherTeamActiveCompanyOptions
      }
      addToAnotherTeamActiveCompany={addToAnotherTeamActiveCompany}
      onAddToAnotherTeamActiveCompanyChange={
        onAddToAnotherTeamActiveCompanyChange
      }
      addToAnotherTeamActiveTeamOptions={addToAnotherTeamActiveTeamOptions}
      addToAnotherTeamActiveTeam={addToAnotherTeamActiveTeam}
      onAddToAnotherTeamActiveTeamChange={setAddToAnotherTeamActiveTeam}
      changeActiveTeamDialogVisible={changeActiveTeamDialogVisible}
      onCloseChangeActiveTeamDialog={resetChangeActiveTeamDialog}
      submittingChangeActiveTeam={submittingChangeActiveTeam}
      onSubmitChangeActiveTeam={submitChangeActiveTeam}
      loadingChangeActiveTeamModal={loadingChangeActiveTeamModal}
      changeActiveTeamCompany={changeActiveTeamCompany}
      changeActiveTeamTeam={changeActiveTeamTeam}
      hasChangeActiveTeamPendingSave={hasChangeActiveTeamPendingSave}
      changeActiveTeamUser={changeActiveTeamUser}
      changeActiveTeamCompanyOptions={changeActiveTeamCompanyOptions}
      onChangeActiveTeamCompanySelect={onChangeActiveTeamCompanySelect}
      changeActiveTeamTeamOptions={changeActiveTeamTeamOptions}
      onChangeActiveTeamTeamChange={setChangeActiveTeamTeam}
      editDialogVisible={editDialogVisible}
      onHideEditDialog={onHideEditDialog}
      editRow={editRow}
      editRole={editRole}
      onCancelEditDialog={onCancelEditDialog}
      onSaveEditDetails={saveEditDetails}
      updateEmployeeDetailsPending={updateEmployeeDetailsPending}
      hasEditFormChanges={hasEditFormChanges}
      editDeskId={editDeskId}
      onEditDeskIdChange={onEditDeskIdChange}
      onEditRoleChange={onEditRoleChange}
      editTimeZone={editTimeZone}
      onEditTimeZoneChange={onEditTimeZoneChange}
      timezoneOptions={timezoneOptions}
      pickActiveTeamDialogVisible={pickActiveTeamDialogVisible}
      onClosePickActiveTeamDialog={resetPickActiveTeamDialog}
      removeUserFromTeamPending={removeUserFromTeamPending}
      onConfirmDeleteAfterPickingActiveTeam={
        confirmDeleteAfterPickingActiveTeam
      }
      selectedReplacementTeam={selectedReplacementTeam}
      replacementTeamOptions={replacementTeamOptions}
      onSelectedReplacementTeamChange={setSelectedReplacementTeam}
      companiesError={companiesError}
      teamsError={teamsError}
      rosterError={rosterError}
      rosterQueryError={rosterQueryError}
      employees={employees}
      loadingEmployees={loadingEmployees}
      searchTerm={searchTerm}
      onSearchTermChange={onSearchTermChange}
      sortedEmployees={sortedEmployees}
      pagedEmployees={pagedEmployees}
      onSortClick={handleSortClick}
      sortedBy={sortedBy}
      reverseSort={reverseSort}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={PAGE_SIZE}
      onActionsButtonClick={onActionsButtonClick}
      userUID={userUID}
      IntegrationImportTabComponent={IntegrationImportTab}
      showAddTeamModal={showAddTeamModal}
      onCloseAddTeamModal={onCloseAddTeamModal}
      onTeamCreated={handleTeamCreated}
      addTeamDefaultCompanyId={addTeamDefaultCompanyId}
    />
  );
};
